import React, { type FunctionComponent } from "react";
import { ArrowLeftOutlined, ReloadOutlined } from "@ant-design/icons";
import { Button, Card, message, Popconfirm, Space, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  ClientImportAggregation,
  ClientImportStatus,
  resolveImportAccessSource,
  type ProductClientToImportDocument,
} from "@fitness-app/data-models/entities/ProductClientToImport";

import { IMPORT_ROW_STATUS_COLOR, importReasonKey, isKnownImportSource } from "./importPresentation";
import { ImportSummaryCounters } from "./ImportSummaryCounters";
import { useProductImport, useRetryProductImport } from "./useProductImports";

interface OwnProps {
  productId: string;
}

const accessSourceLabel = (
  row: ProductClientToImportDocument,
  t: (key: string, options?: Record<string, unknown>) => string,
) => {
  const source = resolveImportAccessSource(row);

  switch (source.kind) {
    case "linked":
      return t("import.accessSource.linkedShort");
    case "price":
      return t("import.accessSource.priceShort");
    default:
      return t("import.accessSource.manualShort");
  }
};

export const ProductImportDetails: FunctionComponent<OwnProps> = ({ productId }) => {
  const { t } = useTranslation(["products", "common"]);
  const { importId } = useParams<{ importId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useProductImport(productId, importId ?? null);
  const retry = useRetryProductImport(productId);

  const backToList = () => navigate(`/products/${productId}/clients`);

  const onRetry = async () => {
    if (!importId) {
      return;
    }

    try {
      const result = await retry.mutateAsync(importId);

      if (result.retried === 0) {
        void message.info(t("import.details.nothingToRetry"));

        return;
      }

      void message.success(t("import.details.retried", { count: result.retried }));
      navigate(`/products/${productId}/clients/imports/${result.importId}`);
    } catch {
      void message.error(t("import.cannotImportClients"));
    }
  };

  const aggregation = data?.aggregation;
  const finished = aggregation?.status === ClientImportAggregation.Finished;

  return (
    <Card
      loading={isLoading}
      title={
        <Space align="center">
          <Button icon={<ArrowLeftOutlined />} onClick={backToList} />
          <span>
            {t("import.details.title")}
            {aggregation ? ` · ${dayjs(aggregation.createdAt).format("DD.MM.YYYY HH:mm")}` : ""}
          </span>
          {aggregation && isKnownImportSource(aggregation.source) ? (
            <Tag>{t(`import.source.${aggregation.source}`)}</Tag>
          ) : null}
        </Space>
      }
      extra={
        finished && aggregation.failed > 0 ? (
          <Popconfirm title={t("import.details.retryConfirm")} onConfirm={() => void onRetry()}>
            <Button icon={<ReloadOutlined />} loading={retry.isLoading}>
              {t("import.details.retryFailed", { count: aggregation.failed })}
            </Button>
          </Popconfirm>
        ) : null
      }
    >
      {isError ? (
        <Typography.Text type="danger">{t("import.details.notFound")}</Typography.Text>
      ) : aggregation ? (
        <Space direction="vertical" size={24} style={{ width: "100%" }}>
          <ImportSummaryCounters aggregation={aggregation} />
          <Table<ProductClientToImportDocument>
            size="small"
            rowKey="id"
            dataSource={data?.clients ?? []}
            pagination={{ pageSize: 50, hideOnSinglePage: true }}
          >
            <Table.Column<ProductClientToImportDocument> title="LP" dataIndex="count" key="count" width={60} />
            <Table.Column<ProductClientToImportDocument>
              title={t("productTransactions.tableHeader.email")}
              dataIndex="email"
              key="email"
              render={(email: string, row) =>
                row.status === ClientImportStatus.Imported ||
                row.status === ClientImportStatus.SkippedDuplicate ||
                row.status === ClientImportStatus.Extended ||
                row.status === ClientImportStatus.Relinked ? (
                  <Link to={`/products/${productId}/clients/${email}`}>{email}</Link>
                ) : (
                  email
                )
              }
            />
            <Table.Column<ProductClientToImportDocument>
              title={t("productTransactions.tableHeader.name")}
              key="name"
              render={(_, row) => `${row.firstName} ${row.lastName}`.trim()}
            />
            <Table.Column<ProductClientToImportDocument>
              title={t("import.accessSource.column")}
              key="accessSource"
              render={(_, row) => accessSourceLabel(row, t)}
            />
            <Table.Column<ProductClientToImportDocument>
              title={t("import.headerForm.accessTo")}
              dataIndex="accessTo"
              key="accessTo"
              render={(accessTo: string | null) => (accessTo ? dayjs(accessTo).format("DD.MM.YYYY") : "-")}
            />
            <Table.Column<ProductClientToImportDocument>
              title={t("import.list.columns.status")}
              dataIndex="status"
              key="status"
              render={(status: ClientImportStatus, row) => {
                const reasonKey = importReasonKey(row.errorMessage);

                return (
                  <Space direction="vertical" size={0}>
                    <Tag color={IMPORT_ROW_STATUS_COLOR[status] ?? "default"}>{t(`import.rowStatus.${status}`)}</Tag>
                    {row.errorMessage && status !== ClientImportStatus.Imported ? (
                      <Typography.Text type="secondary" className="text-xs">
                        {reasonKey ? t(`import.reason.${reasonKey}`) : row.errorMessage}
                      </Typography.Text>
                    ) : null}
                  </Space>
                );
              }}
            />
          </Table>
        </Space>
      ) : null}
    </Card>
  );
};
